import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { DeviceFilters, DeviceOrder } from '@/gql/graphql'

const DevicesQuery = graphql(`
  query Devices($filters: DeviceFilters, $order: DeviceOrder, $after: String) {
    devices(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        name
        dateFormat
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: DeviceFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(DevicesQuery, { variables: { filters } })

  const { pageInfo } = usePageInfo(data?.devices?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<DeviceFilters, DeviceOrder>
        { ...props }
        defaultSort={{ name: 'asc' }}
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.devices.count }
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.devices.nodes = dedupe([...previousData.devices.nodes, ...fetchMoreResult.devices.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        filters={ [
          {
            index: 0,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'nameCont',
          },
        ] }
        headings={ [
          { name: 'Name', sortField: 'name', defaultDirection: 'asc' },
          { name: 'Date format' },
        ] }
        rows={ data?.devices?.nodes.map(device => ({
          id: device.id,
          columns: [
            device.name,
            device.dateFormat?.toUpperCase()
          ],
          link: `/devices/${device.id}`,
        })) }
      /> }
    </>
  )
}
